import { LayoutType, ServiceEntityType } from '@consigli/types';

import { ValidationResult } from '@/utils/validation';

import { ServiceFormData } from './types';
import { validationRules } from './validation';

/**
 * Field requirement mapping for service forms
 */
const fieldRequirements: Record<keyof ServiceFormData, (data: ServiceFormData) => boolean> = {
  serviceType: () => true,
  name: () => true,
  packageType: (data) => (Object.values(ServiceEntityType) as string[]).includes(data.serviceType),
  language: (data) => data.serviceType === ServiceEntityType.DOCUMENT_PACKAGE,
  inputType: (data) => data.serviceType === ServiceEntityType.BUILDING_PACKAGE,
  comments: () => false,
  floorHeight: (data) =>
    data.serviceType === ServiceEntityType.LAYOUT_OPTIMIZATION &&
    data.packageType === LayoutType.PLANT_ROOM,
  files: (data) =>
    (data.serviceType === ServiceEntityType.BUILDING_PACKAGE &&
      ['files', 'json'].includes(data.inputType || '')) ||
    data.serviceType === ServiceEntityType.LAYOUT_OPTIMIZATION,
};

/**
 * Field validation context mapping
 */
const fieldContext: Record<keyof ServiceFormData, (data: ServiceFormData) => unknown[]> = {
  serviceType: () => [],
  name: () => [],
  packageType: (data) => [data.serviceType],
  language: (data) => [data.serviceType],
  inputType: (data) => [data.serviceType],
  comments: () => [],
  floorHeight: (data) => [data.serviceType, data.packageType],
  files: (data) => [data.serviceType, data.inputType],
};

/**
 * Validate all fields in the service form
 */
export const validateServiceForm = (data: ServiceFormData): ValidationResult<ServiceFormData> => {
  const errors: Partial<Record<keyof ServiceFormData, string>> = {};

  Object.keys(data).forEach((field) => {
    const fieldKey = field as keyof ServiceFormData;
    const rule = validationRules[fieldKey];
    if (!rule) return;

    const isRequired = fieldRequirements[fieldKey](data);
    const hasValue = data[fieldKey] != null;

    if (isRequired || (rule.required === false && hasValue)) {
      const context = fieldContext[fieldKey](data);
      const error = rule.validate(data[fieldKey], ...context);
      if (error) {
        errors[fieldKey] = error;
      }
    }
  });

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

// Export validation rules for potential external use
export { validationRules };
